import React, { useState } from "react";
import { Button, Drawer, Form, FormInstance, Input, Select, Switch, Table, Tooltip } from "antd";
import { ReloadOutlined, PlusCircleOutlined, MinusCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import Checkbox, { CheckboxChangeEvent } from "antd/es/checkbox";
import { groupAndSortByKey } from "common/utils/helpers";
import { Application, Permission, RoleFormValues, UpdatedPermission, Dashboard } from "./RoleInterfaces";
import { useDrawerScrollMargin } from "common/utils/useScrollPosition";
import ActionButton from "stories/components/ActionButton/ActionButton";

const { Option } = Select;
const { TextArea } = Input;

interface AddRoleProps {
  handlers: {
    showNewRoleDrawer: () => void;
    closeNewRoleDrawer: () => void;
    onFinishNewRole: (values: RoleFormValues, updatedPermissions: UpdatedPermission[]) => void;
    handleRefresh: () => void;
  };
  values: {
    newRoleForm: FormInstance;
    visibleNewRoleDrawer: boolean;
    permissionsList: Permission[];
    applicationsList: Application[];
    dashboardsList: Dashboard[];
    hasPermissions: boolean;
  };
}

const AddRole: React.FC<AddRoleProps> = ({ handlers, values }) => {
  const { showNewRoleDrawer, closeNewRoleDrawer, onFinishNewRole, handleRefresh } = handlers;
  const { newRoleForm, visibleNewRoleDrawer, permissionsList, applicationsList, dashboardsList, hasPermissions } = values;
  useDrawerScrollMargin('', visibleNewRoleDrawer);
  const [displayedPermissions, setDisplayedPermissions] = useState<Permission[]>([]);
  const [updatedPermissions, setUpdatedPermissions] = useState<UpdatedPermission[]>([]);

  const [displayedDashboards, setDisplayedDashboards] = useState<Dashboard[]>([]);

  const onChangePermission = (e: CheckboxChangeEvent, id: string) => {
    const field = e.target.name as keyof UpdatedPermission;
    const value = e.target.checked;

    setUpdatedPermissions((prev) => {
      const existing = prev.find((perm) => perm.permissionId === id);

      if (existing) {
        return prev.map((perm) =>
          perm.permissionId === id ? { ...perm, [field]: value } : perm
        );
      } else {
        return [
          ...prev,
          {
            permissionId: id,
            read: field === "read" ? value : false,
            create: field === "create" ? value : false,
            edit: field === "edit" ? value : false,
            delete: field === "delete" ? value : false,
          },
        ];
      }
    });
  };


  const permissionColumns = [
    {
      title: "",
      dataIndex: "name",
      key: "name",
      width: 150,
    },
    {
      title: "",
      dataIndex: "description",
      key: "description",
      width: 10,
      render: (_: any, record: Permission) => (
        <Tooltip title={record.description} color={"var(--tertiary-color)"} placement={"left"}>
          <InfoCircleOutlined style={{ color: "var(--tertiary-color)" }} />
        </Tooltip>
      ),
    },
    {
      title: "View",
      dataIndex: "read",
      key: "read",
      render: (_: any, record: Permission) =>
        record.supportsRead ? (
          <Checkbox name="read" defaultChecked={false} onChange={(e) => onChangePermission(e, record.id)} />
        ) : (
          <Checkbox name="read" disabled />
        ),
    },
    {
      title: "Create",
      dataIndex: "create",
      key: "create",
      render: (_: any, record: Permission) =>
        record.supportsCreate ? (
          <Checkbox name="create" defaultChecked={false} onChange={(e) => onChangePermission(e, record.id)} />
        ) : (
          <Checkbox name="create" disabled />
        ),
    },
    {
      title: "Edit",
      dataIndex: "edit",
      key: "edit",
      render: (_: any, record: Permission) =>
        record.supportsEdit ? (
          <Checkbox name="edit" defaultChecked={false} onChange={(e) => onChangePermission(e, record.id)} />
        ) : (
          <Checkbox name="edit" disabled />
        ),
    },
    {
      title: "Delete",
      dataIndex: "delete",
      key: "delete",
      render: (_: any, record: Permission) =>
        record.supportsDelete ? (
          <Checkbox name="delete" defaultChecked={false} onChange={(e) => onChangePermission(e, record.id)} />
        ) : (
          <Checkbox name="delete" disabled />
        ),
    },
  ];

  const groupedPermissions = groupAndSortByKey(displayedPermissions, "category");
  const renderedPermissions: JSX.Element[] = [];

  for (const [category, list] of Object.entries(groupedPermissions)) {
    const permissionGroup = (
      <Table
        columns={permissionColumns}
        dataSource={(list as Permission[]).sort((a, b) => a.name.localeCompare(b.name))}
        title={() => category}
        key={category}
        pagination={false}
        className="role-table"
        rowKey="id"
      />
    );

    renderedPermissions.push(permissionGroup);
  }

  const handleChangeApplication = (value: string) => {
    const filteredPermissionsList = permissionsList.filter(
      (permission) => permission.customerApplication === value
    );

    setDisplayedPermissions(filteredPermissionsList);

    const hydratedUpdatedPermissions = filteredPermissionsList.map((perm) => ({
      permissionId: perm.id,
      read: false,
      create: false,
      edit: false,
      delete: false,
    }));

    setUpdatedPermissions(hydratedUpdatedPermissions);

    const filteredDashboardsList = dashboardsList.filter(
      (dashboard) => dashboard.customerApplication === value
    );

    setDisplayedDashboards(filteredDashboardsList);
  };

  const closeDrawer = () => {
    closeNewRoleDrawer();
    setDisplayedPermissions([]);
    setUpdatedPermissions([]);
  };

  return (
    <>
      <ActionButton
        onClick={showNewRoleDrawer}
        text="New Role"
        type="primary"
        disabled={!hasPermissions}
        icon={<PlusCircleOutlined />}
      />
      <Drawer
        title="Add New Role"
        placement="right"
        onClose={closeDrawer}
        open={visibleNewRoleDrawer}
        width={500}
        closable={true}
        className="manage-pages-drawer"
        forceRender
      >
        <Form
          form={newRoleForm}
          layout="vertical"
          onFinish={(values) => onFinishNewRole(values, updatedPermissions)}
          name="newRoleForm"
          className="new-role-form"
        >
          <Form.Item
            name="nameValue"
            label="Name"
            rules={[{ required: true, message: "Please type the new role name" }]}
          >
            <Input placeholder="Ex: System Admin" />
          </Form.Item>
          <Form.Item
            name="descriptionValue"
            label="Description"
            rules={[{ required: true, message: "Please type the description" }]}
          >
            <TextArea rows={4} placeholder="Ex: Project Manager role for ABC Company" />
          </Form.Item>
          <Form.Item
            label="Active"
            name="isActiveValue"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch defaultChecked={false} />
          </Form.Item>
          <Form.Item
            label="Customer Application"
            name="customerApplicationValue"
            rules={[{ required: true, message: "Please select customer application" }]}
          >
            <Select placeholder="Select application" onChange={handleChangeApplication}>
              {applicationsList?.filter((i) => i.active).map((app) => (
                <Option value={app._id} key={app._id}>
                  {app.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Dashboards">
            {newRoleForm.isFieldTouched("customerApplicationValue") ? (
              displayedDashboards.length ? (
                <Form.Item
                  name="dashboardsValue"
                >
                  <Select
                    mode="multiple"
                    placeholder="Select dashboards"
                    style={{ width: "100%" }}
                  >
                    {displayedDashboards
                      .map((dashboard) => {
                        return (
                          <Select.Option
                            value={dashboard.id}
                            key={dashboard.id}
                            title={dashboard.name}
                          >
                            {dashboard.name}
                          </Select.Option>
                        );
                      })}
                  </Select>
                </Form.Item>
              ) : (
                <p>No dashboards for this Customer Application.</p>
              )
            ) : (
              <p>Please select Customer Application to see the dashboards.</p>
            )}
          </Form.Item>
          <Form.Item label="Permissions">
            {newRoleForm.isFieldTouched("customerApplicationValue") ? (
              displayedPermissions.length ? (
                renderedPermissions
              ) : (
                <p>No permissions for this Customer Application.</p>
              )
            ) : (
              <p>Please select Customer Application to see the permissions.</p>
            )}
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Save Role
            </Button>
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
};

export default AddRole;
